/**
 * Automatic intelligent classifier for Kabyle & French words
 * Re-classifies words assigned to "Général" / uncategorized into appropriate thematic categories.
 */

export interface WordClassificationInput {
  kab: string;
  fr: string;
  type?: string;
}

/**
 * Returns the best thematic category for a word.
 * If the current category is already specific (not 'Général', 'General', or empty), it is preserved.
 * If the current category is 'Général', 'General', or empty, it intelligently deduces the category.
 */
export function classifyWordCategory(word: WordClassificationInput): string {
  const currentType = (word.type || '').trim();

  // Intercept and auto-fix deprecated categories
  if (currentType.toLowerCase().includes('dialogue & expressions') || currentType.toLowerCase().includes('dialogue et expressions')) {
    if (word.kab.toLowerCase().trim() === 'ih' || word.kab.toLowerCase().trim() === 'ala') {
      return 'Questions & Réponses';
    }
    return 'Phrases & Quotidien';
  }

  const isGeneric =
    !currentType ||
    currentType.toLowerCase() === 'général' ||
    currentType.toLowerCase() === 'general' ||
    currentType.toLowerCase() === 'divers' ||
    currentType.toLowerCase() === 'autre' ||
    currentType.toLowerCase() === 'non classé';

  // If already specific, keep it
  if (!isGeneric) {
    return currentType;
  }

  const fr = (word.fr || '').toLowerCase().trim();
  const kab = (word.kab || '').toLowerCase().trim();

  // 0. Double Pronominalisation (Affixes verbaux combinés Datif + Accusatif)
  if (
    /\b(double pronom|pronom double|donne-le|donne-la|donne-les|dis-le|dis-la|dis-les|montre-le|montre-la|montre-les|le lui|la lui|les lui|le leur|la leur|les leur|le-moi|la-moi|les-moi|le-nous|la-nous|te l'ai|te la|te le|me le|me la|me les|nous le|nous la|vous le|vous la)\b/i.test(
      fr
    ) ||
    /(-as-t|-as-tt|-as-ten|-as-tent|-iyi-t|-iyi-tt|-iyi-ten|-iyi-tent|-ak-t|-ak-tt|-ak-ten|-ak-tent|-am-t|-am-tt|-awen-t|-awen-tt|-asen-t|-asen-tt|-asen-ten|-asen-tent|-aɣ-t|-aɣ-tt|-aɣ-ten|-aɣ-tent|-yas-t|-yas-tt|-yas-ten|-yas-tent|-yaɣ-t|-yasen-t)\b/i.test(
      kab
    ) ||
    /^(efk-iyi|efk-as|efk-aɣ|efk-asen|efk-asent|fkiɣ-as|fkiɣ-ak|fkiɣ-am|fkiɣ-awen|fkiɣ-asen|ini-yas|ini-yi|ini-yaɣ|ini-yasen|mel-iyi|mel-as|awi-yi|awi-yas|err-iyi|err-as|nniɣ-as|nniɣ-ak|nniɣ-am|nniɣ-asen|yenna-yas|tenna-yas|nnan-as|mleɣ-as|zenzeɣ-as|ad as-t|ad ak-t|ad am-tt|ad as-tt|ad iyi-t|ad asen-t|ur as-t|ur iyi-t)\b/i.test(
      kab
    )
  ) {
    return 'Double Pronominalisation';
  }

  // 1. Questions & Réponses
  if (
    fr.includes('?') ||
    kab.includes('?') ||
    /^(qui|quoi|que|qu'est-ce|où|quand|comment|pourquoi|combien|est-ce que|oui|non|peut-être)\b/i.test(fr) ||
    /^(anda|melmi|amek|ayɣer|acḥal|anwa|anta|d acu|ih|ala|ahat)\b/i.test(kab)
  ) {
    return 'Questions & Réponses';
  }

  // 2. Salutations & Politesse
  if (
    /\b(bonjour|salut|bonsoir|bienvenue|merci|pardon|excuse|au revoir|adieu|bonne nuit|bon courage|bonne chance|félicitations|bravo|paix|s'il te plaît|s'il vous plaît|je t'en prie|je vous en prie)\b/i.test(
      fr
    ) ||
    /\b(azul|ansuf|tanemmirt|saha|leɛfu|afud|talwit|suref|ttxil|di leɛnaya)\b/i.test(kab)
  ) {
    return 'Salutations & Politesse';
  }

  // 3. Pronoms & Personnes
  if (
    /\b(moi|toi|lui|elle|nous|vous|eux|elles|quelqu'un|personne|tout le monde|homme|femme|garçon|fille|jeune fille|jeune homme|gens|humain)\b/i.test(
      fr
    ) ||
    /\b(nekk|kečč|kemm|netta|nettat|nekni|nekenti|kunwi|kunemti|medden|amdan|argaz|tameṭṭut|aqcic|taqcict)\b/i.test(
      kab
    )
  ) {
    return 'Pronoms & Personnes';
  }

  // 4. Couleurs
  if (
    /\b(blanc|blanche|noir|noire|rouge|vert|verte|bleu|bleue|jaune|marron|gris|grise|rose|orange|violet|violette|doré|argenté)\b/i.test(
      fr
    ) ||
    /\b(amellal|aberkan|azeggwaɣ|azegzaw|awraɣ|amidadi|aqehwi|aẓerwal|ademdam|aweṛdi|ačini|aṛmadi)\b/i.test(
      kab
    )
  ) {
    return 'Couleurs';
  }

  // 5. Le Temps & Les Saisons
  if (
    /\b(temps|heure|minute|seconde|jour|journée|matin|soir|soirée|nuit|midi|hier|aujourd'hui|demain|après-demain|maintenant|autrefois|toujours|jamais|semaine|mois|année|an|printemps|été|automne|hiver|saison)\b/i.test(
      fr
    ) ||
    /\b(ass|ass-a|azekka|iḍelli|selda-zekka|tura|zik|dima|imiren|iḍ|taṣebḥit|tameddit|azal|aseggas|aggur|imalas|tusdidt|tafsut|anebdu|lexrif|tagrest)\b/i.test(
      kab
    )
  ) {
    return 'Le Temps';
  }

  // 6. Nombres & Quantité
  if (
    /\b(zéro|un|une|deux|trois|quatre|cinq|six|sept|huit|neuf|dix|onze|douze|treize|quatorze|quinze|seize|dix-sept|dix-huit|dix-neuf|vingt|trente|quarante|cinquante|soixante|cent|mille|million|premier|deuxième|beaucoup|peu|trop|assez|tout|rien|combien)\b/i.test(
      fr
    ) ||
    /\b(yiwen|yiwet|sin|snat|tlata|kraḍ|rebɛa|kuẓ|xemsa|semmus|setta|sḍis|sebɛa|sa|tmenya|tmanya|tam|tesɛa|tẓa|ɛecra|ɛacra|ḥdac|tnac|teltac|rbeɛṭac|xemsṭac|seṭṭac|sbeɛṭac|tmenṭac|tsaɛṭac|ɛecrin|xemsin|mraw|mya|alf|alef|aṭas|drus|kullec|ulac)\b/i.test(
      kab
    )
  ) {
    return 'Nombres & Quantité';
  }

  // 7. Animaux
  if (
    /\b(chat|chien|chiot|oiseau|oiseaux|aigle|faucon|hibou|chouette|perdrix|poisson|mouche|abeille|guêpe|fourmi|papillon|araignée|serpent|lézard|tortue|grenouille|mouton|brebis|agneau|bélier|chèvre|bouc|vache|bœuf|veau|taureau|cheval|jument|âne|mulet|chameau|dromadaire|lion|tigre|panthère|léopard|loup|renard|chacal|hyène|ours|sanglier|cochon|hérisson|lièvre|lapin|souris|rat|écureuil|singe|gazelle|animal|animaux|bête|insecte|poule|coq|canard)\b/i.test(
      fr
    ) ||
    /\b(aydi|amcic|aqjun|tasekkurt|izem|aserdun|azger|awtul|aɣilas|uccen|izimer|taɣaṭ|tizizwit|ifker|azrem|zzerzumi|afrux|tizemt)\b/i.test(
      kab
    )
  ) {
    return 'Animaux';
  }

  // 8. Nourriture
  if (
    /\b(nourriture|manger|pain|galette|kesra|couscous|semoule|blé|orge|farine|huile|olive|olives|beurre|lait|fromage|viande|poulet|coquelet|poisson|œuf|œufs|fève|fèves|pois|lentille|pois chiche|légume|fruit|pomme|raisin|figue|datte|orange|citron|grenade|abricot|pêche|pastèque|melon|tomate|oignon|ail|poivron|piment|carotte|pomme de terre|frite|frites|canette|soda|gazouz|sandwich|casse-croûte|pizza|glace|cornet|sel|poivre|épice|sucre|miel|eau|thé|café|expresso|jus|boisson|sauce|bouillon|soupe|chorba|lben|crêpe|baghrir|beignet|beignets|pâtisserie|friandise|bonbon|menthe|persil|coriandre|thym|origan|basilic|cumin|cannelle|laurier|herbe|arôme|repas|déjeuner|dîner)\b/i.test(
      fr
    ) ||
    /\b(aɣrum|ibawen|lḥemmeṣ|lfakya|lxeḍra|tirni|aydiṭ|lmerqa|taḥlawit|azemmur|zith|tamemt|tini|tazaṛt|seksu|ayefki|aksum|amellal|aḍil|tazzart|lifrit|lkanet|gazuz|sanwicc|lkasrut|pitsat|aglis|jji|afermaj|aferruj|claḍa|zzrudiyya|ṭumaṭic|lbaṭaṭa|tameqfult|tiɣrifin|lesfenǧ|ccerba|iyi|latay|lqahwa|nneɛnaɛ|meɛdnus|lkesbur|zzɛter|leḥbaq|felgu|lkemmun|lqeṛfa|lkesba)\b/i.test(
      kab
    )
  ) {
    return 'Nourriture';
  }

  // 9. La Maison
  if (
    /\b(maison|foyer|cheminée|porte|fenêtre|toit|mur|chambre|salon|cuisine|salle de bain|étable|jarre|pot|vaisselle|lit|table|chaise|fauteuil|armoire|meuble|tapis|couverture|drap|oreiller|coussin|assiette|cuillère|fourchette|couteau|verre|tasse|bol|marmite|poêle|four|cruche|bouteille|clé|serrure|lampe|ampoule|miroir|rideau|balai|seau|escalier|cour|habitat)\b/i.test(
      fr
    ) ||
    /\b(axxam|tawwurt|lkanun|tasga|akufi|adaynin|ṭṭaq|asqif|afarnu|tabaḥnakt|kuzina|taḍuṭ|tamsilt|tasarut)\b/i.test(
      kab
    )
  ) {
    return 'La Maison';
  }

  // 10. Le Corps & Soins
  if (
    /\b(corps|tête|cheveu|cheveux|front|sourcil|œil|yeux|regard|nez|bouche|lèvre|dent|dents|langue|joue|menton|oreille|oreilles|cou|gorge|épaule|bras|coude|poignet|main|mains|doigt|doigts|ongle|ongles|poitrine|ventre|dos|cuisse|genou|genoux|jambe|jambes|cheville|pied|pieds|talon|orteil|orteils|peau|os|sang|muscle|cœur|poumon|poumons|foie|estomac|cerveau|santé|soin|soins|maladie|malade|malades|douleur|douleurs|blessure|blessures|plaie|fièvre|médicament|médicaments|remède|médecin|docteur|guérison|guéri|rhume|grippe|toux|tousser|éternuer|éternuement|pansement|bandage|compresse|piqûre|vaccin|pharmacie|hôpital|mal à|mal au|j'ai mal)\b/i.test(
      fr
    ) ||
    /\b(aqerru|aqerruy|tiṭ|allen|anzaren|tanzert|imi|tuɣmas|tuɣmest|iles|ameẓẓuɣ|amezzuɣ|imeẓẓuɣen|imezẓuɣen|afus|ifassen|iḍeḍ|iḍudan|aḍar|iḍaren|iḍarren|ul|ulawen|idamen|idammen|iɣes|iɣsan|aṭṭan|aṭṭanen|ddwa|ddwawat|asafar|isafaren|ṭbib|iṭbiben|taṭbibt|imejji|imejjiyen|sbiṭar|isbiṭaren|asegnaf|isegnafen|farmasi|lfarmasi|tacdadt|ticdadin|lfasma|tigezzt|tigezzin|ljerḥ|lejruḥ|ajerraḥ|tawaẓẓa|azeṭṭad|tusut|tusutin|tawla|tawliwin|aqraḥ|iqraḥen|iqreḥ|yeqreḥ|qerḥent|amuḍin|imuḍinen|tamuḍint|timuḍinin|helkeɣ|yeḥla|teḥla|tazmert|ccfa|iɛḍes|aɛḍas|aɛebbuḍ|iɛebbaḍ|aglim|iglimen|anẓad|anẓaden|iccer|iccaren|afud|ifadden|taɣerɣert|taɣect|tafekka|tifekkiwin|lǧetta|tamgert|timgad|turin|tasa|udem|udmawen)\b/i.test(
      kab
    )
  ) {
    return 'Le Corps & Soins';
  }

  // 11. La Famille
  if (
    /\b(famille|parents|père|papa|mère|maman|fils|fille|frère|sœur|grand-père|papy|grand-mère|mamie|ancêtre|ancêtres|oncle|tante|cousin|cousine|neveu|nièce|enfant|enfants|bébé|mari|époux|femme|épouse|couple|mariage|fiançailles|gendre|belle-fille|beau-père|belle-mère)\b/i.test(
      fr
    ) ||
    /\b(tawacult|baba|yemma|mmi|yelli|gma|weltma|jeddi|setti|ɛemmi|xali|ɛemti|xalti|argaz|tameṭṭut|arraw|tarwa|llufan|lejdud|asli|tislit)\b/i.test(
      kab
    )
  ) {
    return 'La Famille';
  }

  // 12. Nature & Météo
  if (
    /\b(arbre|plante|fleur|herbe|feuille|branche|racine|graine|forêt|bois|rivière|fleuve|ruisseau|lac|mer|océan|vague|source|cascade|fontaine|montagne|colline|vallée|rocher|pierre|caillou|sable|terre|sol|ciel|nuage|pluie|neige|glace|vent|tempête|foudre|éclair|tonnerre|orage|soleil|lune|étoile|aurore|crépuscule|écho|brouillard|brume|paysage|feu|flamme|cendre|fumée)\b/i.test(
      fr
    ) ||
    /\b(asif|iɣil|anza|adrar|targa|aḍu|ageffur|adfel|itran|iṭij|aggur|tajeǧǧigt|aseklu|tala|aẓru)\b/i.test(
      kab
    )
  ) {
    return 'Nature & Météo';
  }

  // 13. Émotions & Sensations
  if (
    /\b(émotion|sentiment|amour|aimer|haine|joie|heureux|heureuse|triste|tristesse|peur|angoisse|colère|honte|fierté|fier|jalousie|espoir|courage|doux|douce|amer|amère|sucré|acide|salé|chaud|chaude|froid|froide|dur|dure|lourd|léger|propre|sale|beau|belle|laid|intelligent|sage|fou|gentil|méchant|calme|fatigué|fatigue|soif|faim)\b/i.test(
      fr
    ) ||
    /\b(tayri|asirem|lferḥ|lḥzen|tugdi|zzɛaf|tissmin|leḥya|zzux|aḥnin|ukyis|aḥerfi|ḥlaw|rẓag|yeqqur|alemqaq|laẓ|fad|leɛya|yeḥma|isemmaḍ)\b/i.test(
      kab
    )
  ) {
    return 'Émotions & Sensations';
  }

  // 14. Pensée & Communication
  if (
    /\b(penser|pensée|croire|savoir|connaître|comprendre|oublier|se souvenir|entendre|écouter|voir|regarder|parler|parole|dire|raconter|expliquer|discuter|voix|mot|mots|phrase|langue|langage|secret|vérité|mensonge|apprendre|étudier|enseigner|idée)\b/i.test(
      fr
    ) ||
    /\b(awal|tameslayt|mmeslay|ini|fhem|ẓer|sel|wali|cfu|ttu|xemmem|nadi|sser|tidett|lekdeb|taɣect)\b/i.test(
      kab
    )
  ) {
    return 'Pensée & Communication';
  }

  // 15. Verbes au Passé
  if (
    /^(j'ai|tu as|il a|elle a|nous avons|vous avez|ils ont|elles ont|je suis|tu es|il est|elle est|nous sommes|vous êtes|ils sont|elles sont)\b/i.test(
      fr
    ) ||
    /^(ččiɣ|ruḥeɣ|xedmeɣ|ufiɣ|zriɣ|walaɣ|sliɣ|fhemeɣ|ččan|ruḥen)\b/i.test(kab)
  ) {
    return 'Verbes au Passé';
  }

  // 16. Verbes au Futur & Présent
  if (
    /^(je vais|tu vas|il va|elle va|nous allons|vous allez|ils vont|je ferai|tu feras|il fera|nous ferons)\b/i.test(
      fr
    ) ||
    /^(ad |yett|nett|sett)\b/i.test(kab)
  ) {
    return 'Verbes au Futur & Présent';
  }

  // 17. Jeux & Transports
  if (
    /\b(jeu|jouer|jouet|balle|ballon|course|transport|voiture|auto|véhicule|camion|bus|train|chemin de fer|métro|avion|bateau|vélo|moto|charrette|voyage|voyager|voyageur|gare|station|port|aéroport)\b/i.test(
      fr
    ) ||
    /\b(urar|uṭaksi|akamyun|lbabuṛ|aṭiyara|apiskilit|abrid n wuzzal|agur)\b/i.test(
      kab
    )
  ) {
    return 'Jeux & Transports';
  }

  // 18. Histoires & École
  if (
    /\b(histoire|conte|légende|proverbe|chanson|poème|poésie|culture|tradition|école|classe|maître|élève|cahier|livre|stylo|crayon|ardoise|leçon|tajmaɛt|assemblée|sagesse)\b/i.test(
      fr
    ) ||
    /\b(amacahu|tamacahut|tinḍi|tasekla|tira|adlis|taktabt|aɣerbaz|amusnaw|tajmaɛt|lqanun)\b/i.test(
      kab
    )
  ) {
    return 'Histoires & École';
  }

  // 19. Lieux & Espace
  if (
    /\b(lieu|endroit|village|ville|pays|territoire|région|rue|chemin|route|sentier|place|marché|souk|fontaine|champ|jardin|pont|monde|terre|nord|sud|est|ouest|droite|gauche|haut|bas|dessus|dessous|devant|derrière|loin|près|ici|là-bas)\b/i.test(
      fr
    ) ||
    /\b(tamurt|taddart|tiɣremt|abrid|tizi|azaghar|lǧemɛa|amkan|tama|zdat|deffir|nnig|ddaw)\b/i.test(
      kab
    )
  ) {
    return 'Lieux & Espace';
  }

  // 20. Mots de liaison
  if (
    /\b(et|ou|mais|donc|car|parce que|puisque|comme|si|lorsque|quand|alors|ensuite|puis|après|avant|pendant|durant|afin de|pour que|c'est pour cela|c'est pourquoi|pour|avec|sans|sous|sur|dans|vers|chez|contre|entre|parmi|par|malgré|pourtant|comme si|on dirait que|tant que|depuis que|sinon)\b/i.test(
      fr
    ) ||
    /\b(d|neɣ|maca|acku|imi|almi|ma|mi|qbel|uqbel|mbeɛd|ula|lemmer|ɣas|deg|mebla|seg|si|ɣer|axaṭer|daymi|iwakken|asmi|syin|imir-en|segmi|skud|send|lameɛna|wanag|wama|ulamma|am|amzun|ɣef|gar)\b/i.test(
      kab
    )
  ) {
    return 'Mots de liaison';
  }

  // 21. Action / Verbe
  if (
    /\b(marcher|courir|sauter|voler|nager|monter|descendre|tomber|entrer|sortir|partir|arriver|venir|aller|retourner|rester|dormir|travailler|faire|porter|prendre|donner|acheter|vendre|chercher|trouver|ouvrir|fermer|casser|laver|nettoyer|cuisiner|couper|chasser|pêcher|jouer|chanter|danser|écrire|lire|frapper|aider|soigner)\b/i.test(
      fr
    )
  ) {
    return 'Verbes';
  }

  // 22. Phrase / Expression quotidienne
  if (fr.split(' ').length >= 3 || kab.split(' ').length >= 3) {
    return 'Phrases & Quotidien';
  }

  // Fallback: assign to 'Phrases & Quotidien' or 'Lieux & Espace' rather than uncategorized
  return 'Phrases & Quotidien';
}
